const packageInfo = require('../package');

import React, { useRef, Suspense, useEffect } from 'react';
import { CSSTransition } from 'react-transition-group';
import ReactDOM from 'react-dom';
import { Router } from 'react-router';
import { createBrowserHistory, History } from 'history';
import { I18nextProvider } from 'react-i18next';
//@ts-ignore
import singleSpaReact from 'single-spa-react';
import {
  useOutsideClick,
  useCancelScrollPage,
  Button,
  IconSvg,
  Box,
  Colors,
  isMobile,
  Separator,
} from '@utex/ui-kit';
import { ApolloProvider, ApolloClient, NormalizedCacheObject } from '@apollo/client';
import { observer } from 'mobx-react';

import { Props } from './types/Props';
import { Container, Wrapper, ButtonClose } from './styles';
import { module as sidemenuModule } from './localization';
import { Navigation } from './components/SideNavigation';
import { LogoutBlock } from './components/LogoutBlock';
import { Links } from './components/SideLinks';
import { DepositButtons } from './components/DepositButtons';
import { UserBlock } from './components/UserBlock';
import { I18Service } from './services/i18nservice';
import { createApolloClient } from './services/createApolloClient';
import { LanguageSwitcher } from './components/LanguageSwitcher';
import { LoginWith2FARecomendation } from './components/LoginWith2FARecomendation';
import { AppButtons } from './components/AppButtons';

export { Props } from './types/Props';

const duration = 300;

let i18n: I18Service;
let apolloClient: ApolloClient<NormalizedCacheObject>;
let history: History<any>;

const SideMenu: React.FC<Props> = ({
  appConfig,
  locale,
  setLocale,
  isVisible,
  setIsVisible,
  isAuthorized,
  onRegistration,
  onLogin,
  onLogout,
  openDepositModal,
  openWithdrawModal,
  openLangModal,
  children,
  navigateTo,
  customNavCallbacks,
  navigationActiveItem,
  loginWith2FARecomendation,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);

  const passportURL = appConfig.utPassport.url;

  const navCallbacks = customNavCallbacks
    ? customNavCallbacks
    : {
        toIdeas: () => navigateTo('/investments'),
        toPortfolio: () => navigateTo('/investments/open-investment'),
        toIPO: () => navigateTo('/how-it-works/ipo'),
        toPreIPO: () => navigateTo('/how-it-works/pre-ipo'),
        toAccount: () => navigateTo('/investments/open-investment'),
        toWithdraw: openWithdrawModal,
        toDeposit: openDepositModal,
        toTrading: () => window.open('/trading'),
        toPartnership: () => window.open('https://partners.utex.io'),
        toPassport: () => window.open(passportURL),
        toAccountMobile: () => navigateTo('/investments/open-investment'),
      };

  const [cancelScrollPage, restoreScrollPage] = useCancelScrollPage();

  useOutsideClick(containerRef, isVisible, () => {
    /**
     * Приходится асинхронить этот колбэк, чтобы колбек useOutsideClick
     * тултипа срабатывал раньше.
     * Иначе handler от useOutsideClick тултипа убивается при закрытии меню,
     * так как тултип находится внутри этого меню
     */
    setTimeout(function () {
      setIsVisible(false);
    }, 0);
  });

  useEffect(() => {
    return restoreScrollPage;
  }, []);

  useEffect(() => {
    apolloClient.resetStore();
  }, [isAuthorized]);

  const cancelScrollPageProps = isMobile()
    ? {
        onEnter: cancelScrollPage,
        onExited: restoreScrollPage,
      }
    : {};

  return (
    <Router history={history}>
      <ApolloProvider client={apolloClient}>
        {children &&
          React.cloneElement(children, {
            onClick: (e: React.SyntheticEvent) => {
              e.stopPropagation();
              const onClick = children.props.onClick;
              onClick && onClick();
            },
          })}

        <CSSTransition
          in={isVisible}
          timeout={duration}
          unmountOnExit={true}
          classNames="sidemenu"
          {...cancelScrollPageProps}
        >
          <Container duration={duration} role="dialog" data-testid="sidebar-menu">
            <Wrapper ref={containerRef}>
              <Suspense
                fallback={
                  <Box margin={['auto']}>
                    <IconSvg src="preloader" width={30} height={30} />
                  </Box>
                }
              >
                <I18nextProvider i18n={i18n.i18next}>
                  <ButtonClose>
                    <Button
                      view="transparent"
                      shape="circle"
                      onClick={() => setIsVisible(false)}
                      aria-label="close menu"
                      data-testid="close-btn"
                    >
                      <IconSvg src="close" color={Colors.text.defaultDim} />
                    </Button>
                  </ButtonClose>

                  <UserBlock
                    isAuthorized={isAuthorized}
                    onRegistration={onRegistration}
                    onLogin={onLogin}
                    profileLink={passportURL}
                    onUserClick={navCallbacks.toPassport}
                    isBottomBorderVisible={!loginWith2FARecomendation?.isVisible}
                  />

                  {loginWith2FARecomendation?.isVisible && (
                    <LoginWith2FARecomendation
                      secure={() => window.open(`${passportURL}/security`)}
                      hide={() => loginWith2FARecomendation?.hide()}
                    />
                  )}

                  <Navigation
                    onAccountClick={navCallbacks.toAccount}
                    onIPOClick={navCallbacks.toIPO}
                    onPreIPOClick={navCallbacks.toPreIPO}
                    onIdeasClick={navCallbacks.toIdeas}
                    onPortfolioClick={navCallbacks.toPortfolio}
                    passportLink={passportURL}
                    activeItem={navigationActiveItem}
                  />

                  {isAuthorized && (
                    <DepositButtons
                      openDeposit={navCallbacks.toDeposit}
                      openWithdraw={navCallbacks.toWithdraw}
                    />
                  )}

                  <Box marginTop="auto">
                    <AppButtons />
                  </Box>

                  <Box paddingTop={15} paddingBottom={20}>
                    <Links
                      onAccountClick={navCallbacks.toAccountMobile}
                      passportLink={passportURL}
                      onPartnershipClick={navCallbacks.toPartnership}
                      onTradingClick={navCallbacks.toTrading}
                    />

                    <Box margin={[5, 0]}>
                      <Separator margin={[0, 24]} color={Colors.ut.border5} />
                    </Box>

                    <LanguageSwitcher
                      locale={locale}
                      setLocale={setLocale}
                      openLangModal={openLangModal}
                    />

                    {isAuthorized && <LogoutBlock onClick={onLogout} />}
                  </Box>
                </I18nextProvider>
              </Suspense>
            </Wrapper>
          </Container>
        </CSSTransition>
      </ApolloProvider>
    </Router>
  );
};

const reactLifecycles = singleSpaReact({
  React,
  ReactDOM,
  rootComponent: observer(SideMenu),
});

export const bootstrap = reactLifecycles.bootstrap;
export const mount = (props: Props) => {
  const { locale, getTokens, appConfig } = props;

  console.groupCollapsed('Application side-menu started');
  console.info('VERSION', packageInfo.version);
  console.groupEnd();

  i18n = new I18Service([sidemenuModule], locale);
  apolloClient = createApolloClient(appConfig.graphqlGateway.url, getTokens);
  history = createBrowserHistory();

  return reactLifecycles.mount(props);
};
export const update = (props: Props) => {
  i18n.update(props);

  return reactLifecycles.update!(props);
};
export const unmount = reactLifecycles.unmount;
